import { useState, useCallback, useRef, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchQuestions } from "@/lib/supabase";
import type { Question } from "@/types/quiz";
import type { QuizState } from "@/types/quiz";
import { toast } from "sonner";
import { processQuestionsWithDifficulty, createFrozenQuestion } from "@/utils/questionCategorization";
import { supabase } from "@/lib/supabase";

interface QuestionSnapshot extends Question {
  selectedAnswer?: number | null;
  isCorrect?: boolean;
}

export function useQuizQuestions(state: QuizState) {
  const [subjectQuestions, setSubjectQuestions] = useState<Question[]>([]);
  const [adaptivePool, setAdaptivePool] = useState<Question[]>([]);
  const [usedQuestionIds, setUsedQuestionIds] = useState<number[]>([]);
  const [currentQuestions, setCurrentQuestions] = useState<Question[]>([]);
  const queryClient = useQueryClient();
  
  const questionSnapshotsRef = useRef<QuestionSnapshot[]>([]);
  const previousDifficultyRef = useRef<string>(state.difficulty);

  // Auto-refresh question pool when difficulty changes mid-quiz
  useEffect(() => {
    if (state.view === 'quiz' && 
        previousDifficultyRef.current !== state.difficulty && 
        state.questionIndex > 0 && // Only after first question
        adaptivePool.length > 0) {
      
      console.log(`Difficulty changed from ${previousDifficultyRef.current} to ${state.difficulty} - refreshing question pool`);
      
      // Get fresh questions for the new difficulty
      const newQuestions = getQuestionsForCurrentDifficulty(adaptivePool, state.difficulty, usedQuestionIds);
      
      if (newQuestions.length > 0) {
        setCurrentQuestions(newQuestions);
        toast.info(`Question difficulty adjusted to ${state.difficulty}`);
      }
    }
    
    previousDifficultyRef.current = state.difficulty;
  }, [state.difficulty, state.view, state.questionIndex, adaptivePool, usedQuestionIds]);

  const { data: questions = [], isLoading: questionsLoading, error } = useQuery({
    queryKey: ['questions', state.subject, state.difficulty],
    queryFn: async () => {
      if (!state.subject) {
        console.log('No subject selected, returning empty array');
        return [];
      }
      
      try {
        console.log('Fetching questions with:', {
          subject: state.subject,
          category: state.category,
          difficulty: state.difficulty,
        });
        
        const fetchedQuestions = await fetchQuestions(
          state.category || 'General Knowledge', 
          state.subject, 
          state.difficulty,
          1000 // Keep 1000 for the pool size
        );

        if (!fetchedQuestions) {
          console.warn('fetchQuestions returned null or undefined');
          return [];
        }

        console.log(`Fetched ${fetchedQuestions.length} questions for pool`);
        
        if (Array.isArray(fetchedQuestions) && fetchedQuestions.length > 0) {
          const processedQuestions = processQuestionsWithDifficulty(fetchedQuestions);
          
          const enrichedQuestions = processedQuestions.map(q => ({
            ...q,
            category: state.category || q.category || 'General Knowledge',
            subject: state.subject || q.subject || 'General Knowledge'
          }));
          
          // Filter out globally used questions
          const availableQuestions = await filterGloballyUsedQuestions(enrichedQuestions, state.subject);
          
          const randomizedPool = [...availableQuestions]
            .sort(() => 0.5 - Math.random())
            .slice(0, Math.min(1000, availableQuestions.length));
          
          console.log(`Created adaptive pool with ${randomizedPool.length} questions after filtering globally used ones`);
          console.log(`Will use ${state.questionsCount} questions for quiz session`);
          
          setAdaptivePool(randomizedPool);
          setUsedQuestionIds([]);
          
          questionSnapshotsRef.current = [];
          
          console.log('Setting questions with auto-categorized difficulty:', enrichedQuestions);
          setSubjectQuestions(enrichedQuestions);
          
          const initialQuestions = getQuestionsForCurrentDifficulty(randomizedPool, state.difficulty, []);
          setCurrentQuestions(initialQuestions);
          
          return initialQuestions;
        }
        
        console.warn('No valid questions returned from fetch');
        return [];
      } catch (err) {
        console.error('Error fetching questions:', err);
        toast.error('Failed to fetch questions. Please try again.');
        throw err;
      }
    },
    enabled: !!state.subject,
    staleTime: 0,
    retry: 1,
    meta: {
      onError: (error: Error) => {
        console.error('Query error:', error);
        toast.error('Error loading questions: ' + error.message);
      }
    }
  });

  const filterGloballyUsedQuestions = async (questions: Question[], subject: string | null) => {
    try {
      // Get globally used questions for this subject
      const { data: usedQuestions, error } = await supabase
        .from('used_questions_global')
        .select('question_id')
        .eq('subject', subject || 'General');
      
      if (error) {
        console.warn('Error fetching globally used questions, proceeding without filter:', error);
        return questions;
      }
      
      const usedQuestionIds = usedQuestions?.map(uq => uq.question_id) || [];
      console.log(`Found ${usedQuestionIds.length} globally used questions for subject: ${subject}`);
      
      const availableQuestions = questions.filter(q => !usedQuestionIds.includes(q.id as number));
      
      // If we have very few questions left (less than required), reset the global tracking for this subject
      if (availableQuestions.length < (state.questionsCount || 20)) {
        console.log(`Only ${availableQuestions.length} unused questions available, resetting global tracking for subject: ${subject}`);
        await resetSubjectGlobalTracking(subject);
        toast.info(`Question pool refreshed for ${subject} - starting with fresh questions!`);
        return questions; // Return all questions after reset
      }
      
      return availableQuestions;
    } catch (error) {
      console.warn('Error in filterGloballyUsedQuestions, proceeding without filter:', error);
      return questions;
    }
  };

  const resetSubjectGlobalTracking = async (subject: string | null) => {
    try {
      const { error } = await supabase.rpc('reset_used_questions_by_subject', {
        subject_name: subject || 'General'
      });
      
      if (error) {
        console.error('Error resetting subject global tracking:', error);
      } else {
        console.log(`Successfully reset global tracking for subject: ${subject}`);
      }
    } catch (error) {
      console.error('Error in resetSubjectGlobalTracking:', error);
    }
  };

  const getQuestionsForCurrentDifficulty = useCallback((
    pool: Question[], 
    difficulty: string, 
    usedIds: number[]
  ) => {
    if (!pool || pool.length === 0) return [];
    
    // Use the state.questionsCount for consistency
    const questionsCount = state.questionsCount ?? 20;
    console.log(`Getting ${questionsCount} questions for difficulty: ${difficulty}`);
    
    const matchingDifficultyQuestions = pool.filter(q => 
      q.difficulty === difficulty && 
      !usedIds.includes(q.id as number)
    );
    
    console.log(`Found ${matchingDifficultyQuestions.length} unused questions matching difficulty '${difficulty}'`);
    
    if (matchingDifficultyQuestions.length >= questionsCount) {
      return matchingDifficultyQuestions.slice(0, questionsCount);
    }
    
    const unusedQuestions = pool.filter(q => !usedIds.includes(q.id as number));
    console.log(`Found ${unusedQuestions.length} total unused questions to supplement`);
    
    return unusedQuestions.slice(0, questionsCount);
  }, [state.questionsCount]);

  const getNextQuestions = useCallback((difficulty: string) => {
    return getQuestionsForCurrentDifficulty(adaptivePool, difficulty, usedQuestionIds);
  }, [adaptivePool, usedQuestionIds, getQuestionsForCurrentDifficulty]);

  const markQuestionAsUsed = useCallback(async (questionId: number) => {
    setUsedQuestionIds(prev => {
      if (prev.includes(questionId)) return prev;
      console.log(`Marking question ${questionId} as used locally`);
      return [...prev, questionId];
    });
    
    // Also mark globally in the database
    try {
      const { error } = await supabase.rpc('mark_question_used_globally', {
        p_question_id: questionId,
        p_subject: state.subject || 'General',
        p_category: state.category || 'General'
      });
      
      if (error) {
        console.warn('Error marking question as used globally:', error);
      } else {
        console.log(`Successfully marked question ${questionId} as used globally`);
      }
    } catch (error) {
      console.warn('Error in global question tracking:', error);
    }
  }, [state.subject, state.category]);

  const addQuestionSnapshot = useCallback((question: Question, index: number) => {
    const frozenQuestion = createFrozenQuestion(question);
    
    frozenQuestion.category = state.category || question.category || 'General Knowledge';
    frozenQuestion.subject = state.subject || question.subject || 'General Knowledge';
    
    console.log('Adding question snapshot with category:', frozenQuestion.category);
    console.log('Adding question snapshot with subject:', frozenQuestion.subject);
    
    if (index >= questionSnapshotsRef.current.length) {
      questionSnapshotsRef.current.push(frozenQuestion);
    } else {
      if (questionSnapshotsRef.current[index].id !== frozenQuestion.id) {
        questionSnapshotsRef.current[index] = frozenQuestion;
      }
    }
    
    console.log(`Added/Updated question snapshot at index ${index}:`, frozenQuestion);
    console.log(`Current snapshots (${questionSnapshotsRef.current.length}):`, 
      questionSnapshotsRef.current.map(q => q.id));
    
    // Mark question as used globally when it's added to snapshots (when user sees it)
    if (frozenQuestion.id) {
      markQuestionAsUsed(frozenQuestion.id as number);
    }
    
    return frozenQuestion;
  }, [state.category, state.subject, markQuestionAsUsed]);

  const updateQuestionAnswer = useCallback((index: number, answer: number) => {
    if (index < 0 || index >= questionSnapshotsRef.current.length) {
      console.error(`Cannot update answer: invalid index ${index}`);
      return;
    }
    
    const question = questionSnapshotsRef.current[index];
    const isCorrect = answer === question.correctanswer;
    
    questionSnapshotsRef.current[index] = {
      ...question,
      selectedAnswer: answer,
      isCorrect
    };
    
    console.log(`Updated answer for question ${question.id} at index ${index}:`, {
      selectedAnswer: answer,
      isCorrect,
      difficulty: question.difficulty,
      category: question.category,
      subject: question.subject
    });
    
    return questionSnapshotsRef.current[index];
  }, []);

  const getQuestionSnapshots = useCallback(() => {
    console.log('Getting question snapshots with categories:', 
      questionSnapshotsRef.current.map(q => q.category));
    console.log('Getting question snapshots with subjects:', 
      questionSnapshotsRef.current.map(q => q.subject));
    return [...questionSnapshotsRef.current];
  }, []);

  const setQuestions = useCallback((newQuestions: Question[]) => {
    if (Array.isArray(newQuestions) && newQuestions.length > 0) {
      const processedQuestions = processQuestionsWithDifficulty(newQuestions);
      
      const enrichedQuestions = processedQuestions.map(q => ({
        ...q,
        category: state.category || q.category || 'General Knowledge',
        subject: state.subject || q.subject || 'General Knowledge'
      }));
      
      console.log('Setting new questions with auto-categorized difficulty:', enrichedQuestions);
      setSubjectQuestions(enrichedQuestions);
      setCurrentQuestions(enrichedQuestions);
    } else {
      console.warn('Attempted to set invalid questions:', newQuestions);
    }
  }, [state.category, state.subject]);

  if (error) {
    console.error('Query error state:', error);
  }

  const resetUsedQuestions = useCallback(() => {
    console.log('Resetting used question IDs for new session');
    setUsedQuestionIds([]);
    questionSnapshotsRef.current = [];
    previousDifficultyRef.current = state.difficulty;
  }, [state.difficulty]);

  return {
    questions: currentQuestions.length > 0 ? currentQuestions : (questions.length > 0 ? questions : subjectQuestions),
    questionsLoading,
    subjectQuestions,
    setQuestions,
    queryClient,
    error,
    adaptivePool,
    usedQuestionIds,
    getNextQuestions,
    markQuestionAsUsed,
    resetUsedQuestions,
    addQuestionSnapshot,
    updateQuestionAnswer,
    getQuestionSnapshots
  };
}
